import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaCaretDown } from "react-icons/fa";
import { AiOutlineLogout } from "react-icons/ai";

import styles from "./styles.module.scss";
import { adminLinks, csuLinks, hopLinks } from "./links";
import { AuthContext } from "../../context";

type AdminLinkProps = {
  name?: string;
  page?: string;
  roles?: string | string[];
  onNavigate?: () => void;
};

const AdminLink = ({
  page = "Admin",
  roles = "Admin",
  onNavigate,
}: AdminLinkProps) => {
  const location = useLocation();
  const { logout } = React.useContext(AuthContext);

  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({});

  const normalizedRoles = Array.isArray(roles) ? roles : [roles];

  const linkRenderer = (currentPage: string) => {
    switch (currentPage) {
      case "CSU":
        return csuLinks;
      case "HOP":
        return hopLinks;
      case "Admin":
      default:
        return adminLinks;
    }
  };

  const hasPermission = (allowedRoles: string[] = []) => {
    if (!allowedRoles?.length) return true;

    return allowedRoles.some((role) => normalizedRoles.includes(role));
  };

  const isChildActive = (children: any[] = []) => {
    return children.some((child) => location.pathname === child.route);
  };

  const toggleHandler = (id: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderLinks = (links: any[]) => {
    return links.map((item: any, index: number) => {
      if (!hasPermission(item?.allowed)) return null;

      const itemKey = `${item.name}-${index}`;
      const hasChildren = Boolean(item.children?.length);
      const childIsActive = hasChildren && isChildActive(item.children);
      const isOpen = openItems[itemKey] || childIsActive;

      if (hasChildren) {
        return (
          <li
            key={itemKey}
            className={`${styles.navItem} ${
              childIsActive ? styles.activeParent : ""
            }`}
          >
            <button
              type="button"
              className={`${styles.navButton} ${
                childIsActive ? styles.activeLink : ""
              }`}
              onClick={() => toggleHandler(itemKey)}
              aria-expanded={isOpen}
            >
              <span className={styles.linkLeft}>
                <span className={styles.linkIcon}>
                  <item.Icon size={19} />
                </span>

                <span className={styles.linkText}>{item.name}</span>
              </span>

              <FaCaretDown
                className={`${styles.caret} ${isOpen ? styles.caretOpen : ""}`}
              />
            </button>

            {isOpen && (
              <ul className={styles.childList}>
                {item.children.map((child: any, childIndex: number) => (
                  <li key={`${child.name}-${childIndex}`}>
                    <NavLink
                      to={child.route}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        `${styles.childLink} ${
                          isActive ? styles.childLinkActive : ""
                        }`
                      }
                    >
                      <span className={styles.childIcon}>
                        <child.Icon size={17} />
                      </span>

                      <span>{child.name}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      }

      return (
        <li key={itemKey} className={styles.navItem}>
          <NavLink
            to={item.route}
            onClick={onNavigate}
            className={({ isActive }) =>
              `${styles.navButton} ${isActive ? styles.activeLink : ""}`
            }
          >
            <span className={styles.linkLeft}>
              <span className={styles.linkIcon}>
                <item.Icon size={19} />
              </span>

              <span className={styles.linkText}>{item.name}</span>
            </span>
          </NavLink>
        </li>
      );
    });
  };

  return (
    <nav className={styles.links} aria-label="Main navigation">
      <ul className={styles.navList}>{renderLinks(linkRenderer(page))}</ul>

      <div className={styles.logoutWrap}>
        <button type="button" className={styles.logoutButton} onClick={logout}>
          <span className={styles.linkIcon}>
            <AiOutlineLogout size={20} />
          </span>

          <span>Logout</span>
        </button>
      </div>
    </nav>
  );
};

export default AdminLink;
